import PropTypes from "prop-types";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useHistory, useLocation, useRouteMatch } from "react-router-dom";
import ControlsHeader from "shared/ControlsHeader";
import SearchBar from "shared/SearchBar";
import Button from "shared/Button";
import { ButtonPreset } from "shared/Button";
import FormActionBar from "shared/@forms/FormActionBar";
import { Modal } from "shared/Modal";
import { FilterColumn } from "shared/FilterColumn";
import Loader from "shared/Loader";
import SortModal from "DreamCast/Shared/SortModal";
import SpeakerListItem from "DreamCast/Shared/SpeakerListItem";
import SpeakerNameForm from "DreamCast/Shared/SpeakerNameForm";
import SpeakerDetailsForm from "DreamCast/Shared/SpeakerDetailsForm";
import SpeakerTypeForm from "DreamCast/Shared/SpeakerTypeForm";
import SpeakerImageUpload from "DreamCast/Shared/SpeakerImageUpload";
import FileUploadSection from "DreamCast/Shared/FileUploadSection";
import FileListSection from "DreamCast/Shared/FileListSection";
import SpeakerAudiosSection from "DreamCast/Shared/SpeakerAudiosSection";
import PlusSquareIcon from "shared/assets/plus-square.svg";
import {
  fetchDreamcastSpeakers,
  fetchDreamcastSpeakerContributorTypes,
  fetchDreamcastSpeakerById,
  createDreamcastSpeaker,
  updateDreamcastSpeaker,
  fetchDreamcastSpeakerArchivedIntros,
  fetchDreamcastSpeakerIntros,
  archiveDreamcastSpeakerIntro,
  unarchiveDreamcastSpeakerIntro,
  deleteDreamcastSpeakerIntros,
  deleteDreamcastSpeaker,
} from "DreamCast/api/speakers";
import {
  uploadDreamcastSpeakerPhotoFile,
  renameSpeakerPhotos,
  deleteSpeakerTempPhotos,
  uploadDreamcastSpeakerIntroFile,
  deleteSpeakerTempIntros,
  moveSpeakerTempIntros,
} from "DreamCast/api/media";
import styles from "./SpeakerList.module.css";

const SORT_OPTIONS = [
  { label: "Speaker: A to Z", value: "speaker-asc" },
  { label: "Speaker: Z to A", value: "speaker-desc" },
];

// Product request: hide intro uploads until a later revision.
const ENABLE_SPEAKER_INTRO_UPLOAD = false;

// Map API response format to internal format
function mapSpeakerFromAPI(apiSpeaker) {
  if (!apiSpeaker) return null;

  const contributorId = String(apiSpeaker.contributorId ?? apiSpeaker.id ?? "");

  const contributorTypeCode =
    apiSpeaker.contributorTypeCode ||
    inferContributorTypeCode({
      imageType: apiSpeaker.imageType,
      isDiamond: Boolean(apiSpeaker.isDiamond),
      isThirdParty: Boolean(apiSpeaker.isThirdParty),
    });

  const derivedFlags = deriveSpeakerFlagsFromTypeCode(contributorTypeCode);

  // Map audios array - convert API format to internal format
  const mappedAudios = (
    Array.isArray(apiSpeaker.audios) ? apiSpeaker.audios : []
  ).map((audio) => ({
    id: String(audio.mediaItemId), // mediaItemId → id (as string for consistency)
    thumbnail: audio.thumbnailPath || "",
    title: audio.title || "",
    date: audio.date || "",
    speaker: audio.contributorName || "",
    episodePart: audio.episodePart || "",
  }));

  return {
    id: contributorId, // Backward-compatible alias used by older code paths.
    contributorId,
    photo: apiSpeaker.thumbnailPath || "",
    name:
      apiSpeaker.name ||
      `${apiSpeaker.contributorName || ""}${
        apiSpeaker.lastName ? ` ${apiSpeaker.lastName}` : ""
      }`.trim() ||
      "Untitled Speaker",
    type: apiSpeaker.type || deriveTypeLabelFromTypeCode(contributorTypeCode),
    language: apiSpeaker.language || "english",
    imageType: apiSpeaker.imageType || derivedFlags.imageType,
    isDraft: Boolean(apiSpeaker.isDraft),
    isThirdParty:
      typeof apiSpeaker.isThirdParty === "boolean"
        ? apiSpeaker.isThirdParty
        : derivedFlags.isThirdParty,
    isDiamond:
      typeof apiSpeaker.isDiamond === "boolean"
        ? apiSpeaker.isDiamond
        : derivedFlags.isDiamond,
    contributorTypeCode,
    contributorType: apiSpeaker.contributorType || "",
    // Internal fields from API
    details: apiSpeaker.details || {},
    intros: Array.isArray(apiSpeaker.intros) ? apiSpeaker.intros : [],
    archivedIntros: Array.isArray(apiSpeaker.archivedIntros)
      ? apiSpeaker.archivedIntros
      : [],
    audios: mappedAudios,
    speakerAPhoto: apiSpeaker.speakerAPhoto || null,
    speakerBPhoto: apiSpeaker.speakerBPhoto || null,
    speakerAName: apiSpeaker.speakerAName || "",
    speakerBName: apiSpeaker.speakerBName || "",
    // Store contributorName and lastName separately so they can be synced back to form fields
    contributorName: apiSpeaker.contributorName || "",
    lastName: apiSpeaker.lastName || "",
  };
}

function withStableRowIds(mappedSpeakers = []) {
  const countsByContributorId = new Map();

  return mappedSpeakers.map((speaker, index) => {
    const contributorId = String(speaker?.contributorId || speaker?.id || "");
    const safeContributorId = contributorId || `unknown-${index}`;
    const currentCount = countsByContributorId.get(safeContributorId) || 0;
    countsByContributorId.set(safeContributorId, currentCount + 1);

    return {
      ...speaker,
      rowId: `${safeContributorId}::${currentCount}`,
    };
  });
}

function preloadSpeakerImages(speakers = []) {
  if (typeof Image === "undefined") {
    return Promise.resolve();
  }

  const urls = Array.from(
    new Set(
      speakers
        .flatMap((speaker) => [
          speaker?.photo,
          speaker?.speakerAPhoto,
          speaker?.speakerBPhoto,
        ])
        .filter(Boolean),
    ),
  );

  return Promise.all(
    urls.map(
      (url) =>
        new Promise((resolve) => {
          const image = new Image();
          const timeoutId = setTimeout(resolve, 10000);
          const finish = () => {
            clearTimeout(timeoutId);
            resolve();
          };
          image.onload = finish;
          image.onerror = finish;
          image.src = url;
        }),
    ),
  );
}

// Map internal format to API format for saving
// Blob URLs are local-only and must never be persisted to the API.
const cdnUrlOrNull = (url) =>
  typeof url === "string" && !url.startsWith("blob:") ? url : null;

function mapSpeakerToAPI(
  internalSpeaker,
  { includeContributorId = true } = {},
) {
  if (!internalSpeaker) return null;

  // Prefer the explicit couple fields so slashes remain part of each name.
  const nameParts = (internalSpeaker.name || "").split(" ");
  const fallbackLastName = nameParts.length > 1 ? nameParts.pop() : "";
  const fallbackContributorName = nameParts.join(" ");
  const lastName =
    typeof internalSpeaker.lastName === "string"
      ? internalSpeaker.lastName
      : fallbackLastName;
  const contributorName =
    typeof internalSpeaker.contributorName === "string"
      ? internalSpeaker.contributorName
      : fallbackContributorName;

  // Extract numeric ID for updates only.
  const contributorIdSource =
    internalSpeaker.contributorId || internalSpeaker.id || "";
  const idMatch = String(contributorIdSource).match(/(\d+)/);
  const numericId = idMatch ? parseInt(idMatch[1], 10) : null;

  // Map audios back to API format
  const mappedAudios = (
    Array.isArray(internalSpeaker.audios) ? internalSpeaker.audios : []
  ).map((audio) => ({
    // Keep string keys (e.g. adm_...) intact; only coerce pure numbers.
    mediaItemId:
      typeof audio.id === "number"
        ? audio.id
        : /^\d+$/.test(String(audio.id || "").trim())
        ? parseInt(String(audio.id).trim(), 10)
        : String(audio.id || "").trim(),
    thumbnailPath: audio.thumbnail || "",
    title: audio.title || "",
    date: audio.date || "",
    contributorName: audio.speaker || "",
    episodePart: audio.episodePart || "",
  }));

  const payload = {
    name: internalSpeaker.name || "", // Full name for validation
    photo: cdnUrlOrNull(internalSpeaker.photo) || "",
    contributorName,
    lastName: lastName,
    type: internalSpeaker.type || "diamond",
    language: internalSpeaker.language || "english",
    imageType: internalSpeaker.imageType || "individual",
    contributorTypeCode:
      internalSpeaker.contributorTypeCode ||
      inferContributorTypeCode({
        imageType: internalSpeaker.imageType || "individual",
        isDiamond: Boolean(internalSpeaker.isDiamond),
        isThirdParty: Boolean(internalSpeaker.isThirdParty),
      }),
    isDraft: Boolean(internalSpeaker.isDraft),
    isThirdParty: Boolean(internalSpeaker.isThirdParty),
    isDiamond: Boolean(internalSpeaker.isDiamond),
    details: internalSpeaker.details || {},
    intros: Array.isArray(internalSpeaker.intros)
      ? internalSpeaker.intros.map(
          // eslint-disable-next-line no-unused-vars
          ({ localPreviewUrl, tempFilename, ...intro }) => intro,
        )
      : [],
    archivedIntros: Array.isArray(internalSpeaker.archivedIntros)
      ? internalSpeaker.archivedIntros.map(
          // eslint-disable-next-line no-unused-vars
          ({ localPreviewUrl, tempFilename, ...intro }) => intro,
        )
      : [],
    audios: mappedAudios,
    speakerAPhoto: cdnUrlOrNull(internalSpeaker.speakerAPhoto),
    speakerBPhoto: cdnUrlOrNull(internalSpeaker.speakerBPhoto),
    speakerAName: internalSpeaker.speakerAName || "",
    speakerBName: internalSpeaker.speakerBName || "",
  };

  if (includeContributorId && numericId) {
    payload.contributorId = numericId;
  }

  return payload;
}

const FILTER_SECTIONS = [
  {
    label: "Type",
    options: [
      { label: "Diamond", value: "diamond" },
      { label: "3rd Party", value: "3rd" },
    ],
  },
  // Remmoved from displaying
  // {
  //   label: "Language",
  //   options: [],
  // },
];

const DEFAULT_SPEAKER_DETAILS = {
  startedBusiness: "",
  wentDiamond: "",
  hometownA: "",
  hometownB: "",
  previousOccupationA: "",
  previousOccupationB: "",
  funFact: "",
};

function inferContributorTypeCode({ imageType, isDiamond, isThirdParty }) {
  if (isThirdParty) {
    return "CONTRIBUTOR_TYPE_THIRD_PARTY";
  }

  if (isDiamond) {
    return imageType === "couple"
      ? "CONTRIBUTOR_TYPE_DIAMOND_COUPLE"
      : "CONTRIBUTOR_TYPE_DIAMOND_INDIVIDUAL";
  }

  return imageType === "couple"
    ? "CONTRIBUTOR_TYPE_COUPLE"
    : "CONTRIBUTOR_TYPE_INDIVIDUAL";
}

function deriveSpeakerFlagsFromTypeCode(contributorTypeCode = "") {
  const upperCode = String(contributorTypeCode || "").toUpperCase();

  return {
    imageType: upperCode.includes("COUPLE") ? "couple" : "individual",
    isDiamond: upperCode.includes("DIAMOND"),
    isThirdParty: upperCode.includes("THIRD_PARTY"),
  };
}

function deriveTypeLabelFromTypeCode(contributorTypeCode = "") {
  const upperCode = String(contributorTypeCode || "").toUpperCase();

  if (upperCode.includes("THIRD_PARTY")) {
    return "3rd";
  }

  if (upperCode.includes("DIAMOND")) {
    return "diamond";
  }

  return "diamond";
}

function splitFullName(value) {
  const trimmed = String(value || "").trim();
  if (!trimmed) {
    return { firstName: "", lastName: "" };
  }

  const parts = trimmed.split(/\s+/);
  if (parts.length === 1) {
    return { firstName: parts[0], lastName: "" };
  }

  return {
    firstName: parts.slice(0, -1).join(" "),
    lastName: parts[parts.length - 1],
  };
}

function splitCoupleDisplayName(value) {
  const trimmed = String(value || "").trim();
  if (!trimmed) {
    return { firstNames: "", lastName: "" };
  }

  const firstAndLast = splitFullName(trimmed);
  return {
    firstNames: firstAndLast.firstName,
    lastName: firstAndLast.lastName,
  };
}

function coupleDisplayNameFromMembers(speakerAName, speakerBName, fallback) {
  const speakerAParts = splitFullName(speakerAName);
  const speakerBParts = splitFullName(speakerBName);
  const hasMemberNames = speakerAName || speakerBName;

  if (!hasMemberNames) {
    return splitCoupleDisplayName(fallback);
  }

  return {
    firstNames: [speakerAParts.firstName, speakerBParts.firstName]
      .filter(Boolean)
      .join(" / "),
    lastName: [speakerAParts.lastName, speakerBParts.lastName]
      .filter(Boolean)
      .join(" / "),
  };
}

function joinNameParts(firstName, lastName) {
  return [String(firstName || "").trim(), String(lastName || "").trim()]
    .filter(Boolean)
    .join(" ");
}

function speakerMatchesIdentifier(speaker, identifier) {
  const normalizedIdentifier = String(identifier || "");
  if (!normalizedIdentifier) {
    return false;
  }

  return (
    String(speaker?.rowId || "") === normalizedIdentifier ||
    String(speaker?.id || "") === normalizedIdentifier ||
    String(speaker?.contributorId || "") === normalizedIdentifier
  );
}

function ensureSpeakerAudioAssignmentIds(audios = []) {
  return (Array.isArray(audios) ? audios : []).map((audio, index) => ({
    ...audio,
    assignmentId:
      typeof audio?.assignmentId === "string" && audio.assignmentId.trim()
        ? audio.assignmentId
        : `${String(audio?.id || "audio")}::${index}::${Date.now()}`,
  }));
}

function orderContributorTypes(types = []) {
  const prioritized = [];
  const remaining = [...types];

  const extractByMatcher = (matcher) => {
    const index = remaining.findIndex(matcher);
    if (index >= 0) {
      const [matched] = remaining.splice(index, 1);
      prioritized.push(matched);
    }
  };

  extractByMatcher((type) => {
    const code = String(type?.contributorTypeCode || "").toUpperCase();
    const label = String(type?.contributorType || "").toLowerCase();
    return (
      code === "CONTRIBUTOR_TYPE_DIAMOND_COUPLE" || label === "diamond couple"
    );
  });

  extractByMatcher((type) => {
    const code = String(type?.contributorTypeCode || "").toUpperCase();
    const label = String(type?.contributorType || "").toLowerCase();
    return (
      code === "CONTRIBUTOR_TYPE_DIAMOND_INDIVIDUAL" ||
      label === "diamond individual"
    );
  });

  const trailing = [];
  const leading = [];

  remaining.forEach((type) => {
    const code = String(type?.contributorTypeCode || "").toUpperCase();
    const label = String(type?.contributorType || "").toLowerCase();
    const shouldTrail =
      code === "CONTRIBUTOR_TYPE_THIRD_PARTY" ||
      code === "CONTRIBUTOR_TYPE_AMWAY" ||
      label === "third party" ||
      label === "amway";

    if (shouldTrail) {
      trailing.push(type);
      return;
    }

    leading.push(type);
  });

  return [...prioritized, ...leading, ...trailing];
}

// DEPRECATED: Mock audio library removed - audios should be managed via API
// Previously AUDIO_LIBRARY had 3 mock audios that were auto-added to speakers
// This has been replaced with API-driven audio management through speaker.audios[]
// Audios now come entirely from the backend API during speaker fetch

function extractSortableLastName(name) {
  const normalized = String(name || "")
    .replace(/[.,]/g, " ")
    .trim();

  if (!normalized) {
    return "";
  }

  const tokens = normalized.split(/\s+/).filter(Boolean);

  if (!tokens.length) {
    return "";
  }

  const ignoredSuffixes = new Set(["jr", "sr", "ii", "iii", "iv"]);

  let index = tokens.length - 1;
  while (index > 0 && ignoredSuffixes.has(tokens[index].toLowerCase())) {
    index -= 1;
  }

  return tokens[index].toLowerCase();
}

function matchesOptionFilter(speakerValue, selectedValues) {
  if (!selectedValues || selectedValues.length === 0) {
    return true;
  }

  return selectedValues.includes(String(speakerValue || "").toLowerCase());
}

function normalizeSpeakerSearchText(value) {
  return String(value || "")
    .replace(/&/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();
}

export function SpeakerList({
  headerTitle,
  initialSpeakers,
  disableInitialFetch,
  initialEditingSpeakerId,
  onAddNewSpeaker,
  onEditSpeaker,
  onEditAudio,
}) {
  const history = useHistory();
  const location = useLocation();
  const { path } = useRouteMatch();
  const pageRef = useRef(null);
  const safeInitialSpeakers = useMemo(
    () => (Array.isArray(initialSpeakers) ? initialSpeakers : []),
    [initialSpeakers],
  );
  const [speakers, setSpeakers] = useState(safeInitialSpeakers);
  const [contributorTypes, setContributorTypes] = useState([]);
  const [selectedContributorTypeCode, setSelectedContributorTypeCode] =
    useState("");
  const [selectedSpeakerIds, setSelectedSpeakerIds] = useState([]);
  // Row ids that were already assigned when selection mode opened; Cancel restores these.
  const [initialSelectedRowIds, setInitialSelectedRowIds] = useState([]);
  const [apiError, setApiError] = useState("");
  const [isLoadingList, setIsLoadingList] = useState(false);

  const loadSpeakers = useCallback(
    async ({ showLoader = true, preloadImages = false } = {}) => {
      if (showLoader) {
        setIsLoadingList(true);
      }
      try {
        const startTime = Date.now();
        const response = await fetchDreamcastSpeakers();

        // Extract speakers from response structure: { data: {...}, success: true }
        let apiSpeakers = [];
        if (response?.data) {
          // Handle { data: [...] } or { data: { speakers: [...] } }
          apiSpeakers = Array.isArray(response.data)
            ? response.data
            : Array.isArray(response.data?.speakers)
            ? response.data.speakers
            : [];
        } else if (Array.isArray(response?.speakers)) {
          // Fallback to old format
          apiSpeakers = response.speakers;
        }

        const mappedSpeakers = withStableRowIds(
          apiSpeakers.map(mapSpeakerFromAPI).filter(Boolean),
        );
        if (preloadImages) {
          await preloadSpeakerImages(mappedSpeakers);
        }
        setSpeakers(mappedSpeakers);

        if (showLoader) {
          // Ensure minimum 500ms display time for loader visibility
          const elapsed = Date.now() - startTime;
          const remainingTime = Math.max(0, 500 - elapsed);
          if (remainingTime > 0) {
            await new Promise((resolve) => setTimeout(resolve, remainingTime));
          }
        }
      } finally {
        if (showLoader) {
          setIsLoadingList(false);
        }
      }
    },
    [],
  );

  useEffect(() => {
    if (disableInitialFetch) {
      setSpeakers(safeInitialSpeakers);
      return;
    }

    loadSpeakers().catch((error) => {
      setApiError(error.message || "Failed to load speakers.");
    });
  }, [disableInitialFetch, loadSpeakers, safeInitialSpeakers]);

  useEffect(() => {
    const loadContributorTypes = async () => {
      const response = await fetchDreamcastSpeakerContributorTypes();
      const rawTypes = Array.isArray(response?.data?.contributorTypes)
        ? response.data.contributorTypes
        : [];
      const types = orderContributorTypes(rawTypes);

      setContributorTypes(types);
      setSelectedContributorTypeCode((current) => current);
    };

    loadContributorTypes().catch((error) => {
      console.warn(
        "[SpeakerList] Failed to load contributor types:",
        error?.message || error,
      );
    });
  }, []);

  const selectedContributorType = useMemo(
    () =>
      contributorTypes.find(
        (type) => type.contributorTypeCode === selectedContributorTypeCode,
      ) || null,
    [contributorTypes, selectedContributorTypeCode],
  );

  const isChooseSelected = !selectedContributorTypeCode;

  const isSelectionMode = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get("mode") === "selection";
  }, [location.search]);

  const selectionSource = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get("source") || "";
  }, [location.search]);

  const preselectedSpeakerIds = useMemo(
    () =>
      Array.isArray(location.state?.preselectedSpeakerIds)
        ? location.state.preselectedSpeakerIds
        : [],
    [location.state],
  );

  const speakerSelectionReturnPath = useMemo(
    () => location.state?.speakerSelectionReturnPath || "",
    [location.state],
  );

  const [isCreateView, setIsCreateView] = useState(false);
  const [editingSpeakerId, setEditingSpeakerId] = useState(null);
  const [editingWasDraft, setEditingWasDraft] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState({});
  const [sortBy, setSortBy] = useState("speaker-asc");

  const [speakerName, setSpeakerName] = useState("");
  const [speakerDetails, setSpeakerDetails] = useState(DEFAULT_SPEAKER_DETAILS);
  const [speakerPhoto, setSpeakerPhoto] = useState(null);
  const [speakerAPhoto, setSpeakerAPhoto] = useState(null);
  const [speakerBPhoto, setSpeakerBPhoto] = useState(null);
  const [speakerImageType, setSpeakerImageType] = useState("individual");
  const [speakerIsThirdParty, setSpeakerIsThirdParty] = useState(false);
  const [speakerIsDiamond, setSpeakerIsDiamond] = useState(false);
  const [coupleFirstNames, setCoupleFirstNames] = useState("");
  const [coupleLastName, setCoupleLastName] = useState("");
  const [speakerAFirstName, setSpeakerAFirstName] = useState("");
  const [speakerALastName, setSpeakerALastName] = useState("");
  const [speakerBFirstName, setSpeakerBFirstName] = useState("");
  const [speakerBLastName, setSpeakerBLastName] = useState("");

  // Track temp photo filenames for rename/delete
  const [speakerPhotoTemp, setSpeakerPhotoTemp] = useState(null);
  const [speakerAPhotoTemp, setSpeakerAPhotoTemp] = useState(null);
  const [speakerBPhotoTemp, setSpeakerBPhotoTemp] = useState(null);

  // Track photo upload progress to prevent saving with incomplete uploads
  const [speakerPhotoUploading, setSpeakerPhotoUploading] = useState(false);
  const [speakerAPhotoUploading, setSpeakerAPhotoUploading] = useState(false);
  const [speakerBPhotoUploading, setSpeakerBPhotoUploading] = useState(false);

  // Track intro file uploads to prevent saving with incomplete uploads
  const [introFilesUploading, setIntroFilesUploading] = useState(0);

  // Track the last-saved CDN URLs so we can delete them when a new photo overwrites them.
  const [speakerPhotoPrev, setSpeakerPhotoPrev] = useState(null);
  const [speakerAPhotoPrev, setSpeakerAPhotoPrev] = useState(null);
  const [speakerBPhotoPrev, setSpeakerBPhotoPrev] = useState(null);

  const [introFiles, setIntroFiles] = useState([]);
  const [archivedIntroFiles, setArchivedIntroFiles] = useState([]);
  const [removedArchivedIntroFiles, setRemovedArchivedIntroFiles] = useState(
    [],
  );
  // Refs mirror state so saveFromCreateForm reads the latest value even before a re-render.
  const introFilesRef = useRef([]);
  const archivedIntroFilesRef = useRef([]);
  const [showArchivedIntros, setShowArchivedIntros] = useState(false);
  const [introUploadedFilenames, setIntroUploadedFilenames] = useState([]);

  const [speakerAudios, setSpeakerAudios] = useState([]);
  const [speakerAudioSearch, setSpeakerAudioSearch] = useState("");
  const [hasAppliedInitialEdit, setHasAppliedInitialEdit] = useState(false);
  const [loadingSpeakerId, setLoadingSpeakerId] = useState(null);
  const [savingState, setSavingState] = useState(null);
  const [newlySavedSpeakerId, setNewlySavedSpeakerId] = useState(null);

  // Optimistic UI: queue background file moves instead of blocking save
  const [pendingFileOps, setPendingFileOps] = useState(null);
  const [backgroundNotification, setBackgroundNotification] = useState(null);

  // Track speakers where user attempted edit during file operations
  const [editAttemptedDuringSpeakerIds, setEditAttemptedDuringSpeakerIds] =
    useState(new Set());

  // Delete confirmation modal state
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [isDeletingState, setIsDeletingState] = useState(false);

  useEffect(() => {
    if (!selectedContributorTypeCode) {
      return;
    }

    const derived = deriveSpeakerFlagsFromTypeCode(selectedContributorTypeCode);
    setSpeakerImageType(derived.imageType);
    setSpeakerIsThirdParty(derived.isThirdParty);
    setSpeakerIsDiamond(derived.isDiamond);
  }, [selectedContributorTypeCode]);

  const activeHeaderTitle = useMemo(() => {
    if (!isCreateView || !editingSpeakerId) {
      return headerTitle;
    }

    const fallbackName =
      (speakers || []).find((speaker) =>
        speakerMatchesIdentifier(speaker, editingSpeakerId),
      )?.name || "";
    const displayName = speakerName.trim() || fallbackName;

    if (!displayName) {
      return headerTitle;
    }

    return `${headerTitle} - ${displayName}`;
  }, [editingSpeakerId, headerTitle, isCreateView, speakerName, speakers]);

  const visibleSpeakers = useMemo(() => {
    const normalizedQuery = normalizeSpeakerSearchText(searchQuery);

    const filtered = (speakers || []).filter((speaker) => {
      if (normalizedQuery) {
        const searchable = `${speaker.name || ""} ${speaker.type || ""} ${
          speaker.language || ""
        } ${speaker.contributorName || ""} ${speaker.lastName || ""} ${
          speaker.speakerAName || ""
        } ${speaker.speakerBName || ""}`;
        if (!normalizeSpeakerSearchText(searchable).includes(normalizedQuery)) {
          return false;
        }
      }

      const selectedType = filters.Type || [];
      // const selectedLanguage = filters.Language || [];

      if (!matchesOptionFilter(speaker.type, selectedType)) {
        return false;
      }

      // Language filter disabled
      // if (!matchesOptionFilter(speaker.language, selectedLanguage)) {
      //   return false;
      // }

      return true;
    });

    const sorted = [...filtered].sort((a, b) => {
      const aLast = extractSortableLastName(a.name);
      const bLast = extractSortableLastName(b.name);

      const lastNameResult = aLast.localeCompare(bLast);
      if (lastNameResult !== 0) {
        return sortBy === "speaker-desc" ? -lastNameResult : lastNameResult;
      }

      const fallbackResult = String(a.name || "").localeCompare(
        String(b.name || ""),
      );

      return sortBy === "speaker-desc" ? -fallbackResult : fallbackResult;
    });

    return sorted;
  }, [filters, searchQuery, sortBy, speakers]);

  // Language filter disabled - keep Type filter working
  const filterSections = useMemo(() => {
    // const languageOptions = Array.from(
    //   new Map(
    //     (Array.isArray(speakers) ? speakers : [])
    //       .map((speaker) => String(speaker?.language || "").trim())
    //       .filter(Boolean)
    //       .map((label) => [
    //         label.toLowerCase(),
    //         { label, value: label.toLowerCase() },
    //       ]),
    //   ).values(),
    // );

    return FILTER_SECTIONS.map(
      (section) =>
        // Language filter disabled
        // section.label === "Language"
        //   ? {
        //       ...section,
        //       options: languageOptions,
        //     }
        //   :
        section,
    );
  }, [speakers]);

  const noResults =
    visibleSpeakers.length === 0 && String(searchQuery || "").trim() !== "";

  const filtersHaveSelection = Object.keys(filters || {}).some(
    (key) => Array.isArray(filters[key]) && filters[key].length > 0,
  );

  const filtersNoResults = visibleSpeakers.length === 0 && filtersHaveSelection;

  const resetCreateForm = () => {
    setEditingSpeakerId(null);
    setEditingWasDraft(false);
    setSpeakerName("");
    setSpeakerDetails(DEFAULT_SPEAKER_DETAILS);
    setSpeakerPhoto(null);
    setSpeakerAPhoto(null);
    setSpeakerBPhoto(null);
    setSpeakerImageType("individual");
    setSpeakerIsThirdParty(false);
    setSpeakerIsDiamond(false);
    setSelectedContributorTypeCode("");
    setCoupleFirstNames("");
    setCoupleLastName("");
    setSpeakerAFirstName("");
    setSpeakerALastName("");
    setSpeakerBFirstName("");
    setSpeakerBLastName("");
    setSpeakerPhotoTemp(null);
    setSpeakerAPhotoTemp(null);
    setSpeakerBPhotoTemp(null);
    setSpeakerPhotoPrev(null);
    setSpeakerAPhotoPrev(null);
    setSpeakerBPhotoPrev(null);
    setIntroFiles([]);
    setArchivedIntroFiles([]);
    setRemovedArchivedIntroFiles([]);
    introFilesRef.current = [];
    archivedIntroFilesRef.current = [];
    setShowArchivedIntros(false);
    setIntroUploadedFilenames([]);
    setSpeakerAudios([]);
    setSpeakerAudioSearch("");
  };

  const openCreateView = () => {
    resetCreateForm();
    setIsCreateView(true);
    onAddNewSpeaker();
  };

  const handleIndividualFirstNameChange = (value) => {
    setSpeakerAFirstName(value);
    setSpeakerName(joinNameParts(value, speakerALastName));
  };

  const handleIndividualLastNameChange = (value) => {
    setSpeakerALastName(value);
    setSpeakerName(joinNameParts(speakerAFirstName, value));
  };

  // Check if a speaker has pending file operations
  const hasPendingFileOps = (speakerId) => {
    if (!pendingFileOps) return false;
    // Normalize IDs to strings for comparison (API returns numbers, list may use strings)
    const speakerIdStr = String(speakerId);
    const introsForThisSpeaker =
      String(pendingFileOps.intros?.speakerId) === speakerIdStr;
    const photosForThisSpeaker =
      String(pendingFileOps.photos?.speakerId) === speakerIdStr;
    const deletedIntrosForThisSpeaker =
      String(pendingFileOps.deletedIntros?.speakerId) === speakerIdStr;
    return (
      introsForThisSpeaker ||
      photosForThisSpeaker ||
      deletedIntrosForThisSpeaker
    );
  };

  const openEditView = async (speaker) => {
    if (!speaker) {
      return;
    }

    // Prevent editing if file operations are still in progress for this speaker
    if (hasPendingFileOps(speaker.id)) {
      // Mark that user attempted to edit this speaker during pending ops
      setEditAttemptedDuringSpeakerIds(
        (current) => new Set([...current, String(speaker.rowId || speaker.id)]),
      );
      setApiError(
        "Cannot edit this speaker while file operations are in progress. Please wait a moment and try again.",
      );
      return;
    }

    setEditingSpeakerId(speaker.rowId || speaker.id);
    setEditingWasDraft(Boolean(speaker.isDraft));
    setSpeakerName(speaker.name || "");
    setSpeakerDetails({
      ...DEFAULT_SPEAKER_DETAILS,
      ...(speaker.details || {}),
    });
    setSpeakerPhoto(speaker.photo || null);
    setSpeakerAPhoto(speaker.speakerAPhoto || null);
    setSpeakerBPhoto(speaker.speakerBPhoto || null);
    setSpeakerPhotoPrev(cdnUrlOrNull(speaker.photo));
    setSpeakerAPhotoPrev(cdnUrlOrNull(speaker.speakerAPhoto));
    setSpeakerBPhotoPrev(cdnUrlOrNull(speaker.speakerBPhoto));
    setSpeakerImageType(
      speaker.imageType ||
        (speaker.speakerAPhoto || speaker.speakerBPhoto
          ? "couple"
          : "individual"),
    );
    setSpeakerIsThirdParty(speaker.isThirdParty ?? speaker.type === "3rd");
    setSpeakerIsDiamond(speaker.isDiamond ?? speaker.type === "diamond");
    setSelectedContributorTypeCode(
      speaker.contributorTypeCode ||
        inferContributorTypeCode({
          imageType:
            speaker.imageType ||
            (speaker.speakerAPhoto || speaker.speakerBPhoto
              ? "couple"
              : "individual"),
          isDiamond: speaker.isDiamond ?? speaker.type === "diamond",
          isThirdParty: speaker.isThirdParty ?? speaker.type === "3rd",
        }),
    );
    const coupleNameParts = coupleDisplayNameFromMembers(
      speaker.speakerAName,
      speaker.speakerBName,
      speaker.name || "",
    );
    
    // Use the API values directly if they exist for couples
    const isCoupleType = speaker.imageType === "couple" ||
      (speaker.speakerAPhoto || speaker.speakerBPhoto);
    
    // For couples, use contributorName and lastName from the API (they're the source of truth)
    // Otherwise fall back to deriving from speakerAName and speakerBName
    if (isCoupleType && typeof speaker.contributorName === "string") {
      setCoupleFirstNames(speaker.contributorName);
      setCoupleLastName(speaker.lastName || "");
    } else {
      setCoupleFirstNames(coupleNameParts.firstNames);
      setCoupleLastName(coupleNameParts.lastName);
    }

    const shouldUseCoupleNames =
      (speaker.imageType ||
        (speaker.speakerAPhoto || speaker.speakerBPhoto
          ? "couple"
          : "individual")) === "couple";

    // Individuals: the stored first/last fields are the source of truth.
    // Re-splitting the full name on its last space breaks multiword last
    // names ("Zoë" + "de Test" -> "Zoë de" + "Test").
    const hasStoredIndividualName =
      !shouldUseCoupleNames &&
      Boolean(
        (typeof speaker.contributorName === "string" &&
          speaker.contributorName.trim()) ||
          (typeof speaker.lastName === "string" && speaker.lastName.trim()),
      );
    const speakerANameParts = hasStoredIndividualName
      ? {
          firstName: speaker.contributorName || "",
          lastName: speaker.lastName || "",
        }
      : splitFullName(
          shouldUseCoupleNames ? speaker.speakerAName || "" : speaker.name || "",
        );
    setSpeakerAFirstName(speakerANameParts.firstName);
    setSpeakerALastName(speakerANameParts.lastName);

    const speakerBNameParts = splitFullName(speaker.speakerBName || "");
    setSpeakerBFirstName(speakerBNameParts.firstName);
    setSpeakerBLastName(speakerBNameParts.lastName);
    setShowArchivedIntros(false);
    setSpeakerAudios(
      ensureSpeakerAudioAssignmentIds(
        Array.isArray(speaker.audios)
          ? speaker.audios.map((audio) => ({ ...audio }))
          : [],
      ),
    );
    setSpeakerAudioSearch("");

    // Load intros from Bunny folder listing so we never need to persist URLs in the DB.
    try {
      const response = await fetchDreamcastSpeakerIntros(
        speaker.contributorId || speaker.id,
      );
      const intros = Array.isArray(response?.data) ? response.data : [];

      setIntroFiles(intros);
      introFilesRef.current = intros;
    } catch (err) {
      console.warn(
        "[openEditView] Failed to load intros:",
        err?.message || err,
      );
      setIntroFiles([]);
      introFilesRef.current = [];
    }
    setArchivedIntroFiles([]);
    archivedIntroFilesRef.current = [];
    setRemovedArchivedIntroFiles([]);

    // Reset temp upload tracking since we're loading an existing speaker's intros
    setIntroUploadedFilenames([]);
    setSpeakerPhotoTemp(null);
    setSpeakerAPhotoTemp(null);
    setSpeakerBPhotoTemp(null);

    setIsCreateView(true);
  };

  useEffect(() => {
    if (hasAppliedInitialEdit || !initialEditingSpeakerId || isCreateView) {
      return;
    }

    const selectedSpeaker = (speakers || []).find((speaker) =>
      speakerMatchesIdentifier(speaker, initialEditingSpeakerId),
    );

    if (selectedSpeaker) {
      openEditView(selectedSpeaker);
    }

    setHasAppliedInitialEdit(true);
  }, [hasAppliedInitialEdit, initialEditingSpeakerId, isCreateView, speakers]);

  // Sync form fields when the current speaker's data is updated in the speakers array
  useEffect(() => {
    if (!editingSpeakerId || isCreateView) {
      return;
    }

    const currentSpeaker = (speakers || []).find((speaker) =>
      speakerMatchesIdentifier(speaker, editingSpeakerId),
    );

    if (currentSpeaker) {
      // Update the form fields with the latest speaker data
      setSpeakerName(currentSpeaker.name || "");
      setSpeakerDetails(currentSpeaker.details || DEFAULT_SPEAKER_DETAILS);
      
      const isCoupleType = currentSpeaker.imageType === "couple" ||
        (currentSpeaker.speakerAPhoto || currentSpeaker.speakerBPhoto ? true : false);

      if (isCoupleType) {
        // For couples, use contributorName and lastName directly from API
        setCoupleFirstNames(currentSpeaker.contributorName || "");
        setCoupleLastName(currentSpeaker.lastName || "");
      } else {
        // For individuals, use contributorName and lastName directly from API
        setCoupleFirstNames("");
        setCoupleLastName("");
      }

      // Also populate the individual speaker fields from speakerAName and speakerBName
      const speakerANameParts = splitFullName(currentSpeaker.speakerAName || "");
      setSpeakerAFirstName(speakerANameParts.firstName);
      setSpeakerALastName(speakerANameParts.lastName);

      const speakerBNameParts = splitFullName(currentSpeaker.speakerBName || "");
      setSpeakerBFirstName(speakerBNameParts.firstName);
      setSpeakerBLastName(speakerBNameParts.lastName);
    }
  }, [editingSpeakerId, speakers, isCreateView]);

  useEffect(() => {
    if (!showArchivedIntros || !editingSpeakerId) {
      return;
    }

    const fetchArchivedIntros = async () => {
      try {
        const response = await fetchDreamcastSpeakerArchivedIntros(
          editingSpeakerId,
        );

        // Extract intros from response structure: { data: {...}, success: true }
        let archivedIntros = [];
        if (response?.data) {
          // Handle { data: [...] }, { data: { intros: [...] } }, or { data: { archivedIntros: [...] } }
          archivedIntros = Array.isArray(response.data)
            ? response.data
            : Array.isArray(response.data?.archivedIntros)
            ? response.data.archivedIntros
            : Array.isArray(response.data?.intros)
            ? response.data.intros
            : [];
        } else if (Array.isArray(response?.archivedIntros)) {
          // Fallback format 1
          archivedIntros = response.archivedIntros;
        } else if (Array.isArray(response?.intros)) {
          // Fallback format 2
          archivedIntros = response.intros;
        }

        setArchivedIntroFiles(archivedIntros);
      } catch (error) {
        // Silent catch
      }
    };

    fetchArchivedIntros();
  }, [showArchivedIntros, editingSpeakerId]);

  // introFiles is kept in sync via onFilesChange; track filenames here for cancel cleanup
  const handleIntroSave = ({ tempFilename }) => {
    if (tempFilename) {
      setIntroUploadedFilenames((current) => [...current, tempFilename]);
    }
  };

  const handleIntroArchive = async (fileId) => {
    if (!editingSpeakerId) return;

    try {
      await archiveDreamcastSpeakerIntro(editingSpeakerId, fileId);

      setIntroFiles((current) => {
        const matched = current.find((file) => file.id === fileId);
        if (!matched) return current;

        setArchivedIntroFiles((currentArchived) => [
          matched,
          ...currentArchived,
        ]);
        return current.filter((file) => file.id !== fileId);
      });
    } catch (error) {
      // Silent catch
      // Still update UI as fallback
      setIntroFiles((current) => {
        const matched = current.find((file) => file.id === fileId);
        if (!matched) return current;

        setArchivedIntroFiles((currentArchived) => [
          matched,
          ...currentArchived,
        ]);
        return current.filter((file) => file.id !== fileId);
      });
    }
  };

  const handleIntroUnarchive = async (fileId) => {
    if (!editingSpeakerId) return;

    try {
      await unarchiveDreamcastSpeakerIntro(editingSpeakerId, fileId);

      setArchivedIntroFiles((current) => {
        const matched = current.find((file) => file.id === fileId);
        if (!matched) return current;

        setIntroFiles((currentActive) => [matched, ...currentActive]);
        return current.filter((file) => file.id !== fileId);
      });
    } catch (error) {
      // Silent catch
      // Still update UI as fallback
      setArchivedIntroFiles((current) => {
        const matched = current.find((file) => file.id === fileId);
        if (!matched) return current;

        setIntroFiles((currentActive) => [matched, ...currentActive]);
        return current.filter((file) => file.id !== fileId);
      });
    }
  };

  const handleArchivedIntroRemove = async (fileId) => {
    setArchivedIntroFiles((current) => {
      const target = current.find((file) => file.id === fileId);
      if (target) {
        setRemovedArchivedIntroFiles((existing) => {
          const alreadyTracked = existing.some(
            (item) => item?.id === target.id,
          );
          return alreadyTracked ? existing : [...existing, target];
        });
      }

      return current.filter((file) => file.id !== fileId);
    });
  };

  const handleSpeakerAudioAdd = () => {
    const baseDreamCastPath = path.replace(/\/speaker-list$/, "");
    const currentSpeakerDisplayName =
      speakerImageType === "couple"
        ? joinNameParts(coupleFirstNames, coupleLastName) || speakerName
        : joinNameParts(speakerAFirstName, speakerALastName) || speakerName;
    const speakerFormState = {
      editingSpeakerId,
      editingWasDraft,
      speakerName,
      speakerDetails,
      speakerPhoto,
      speakerAPhoto,
      speakerBPhoto,
      speakerImageType,
      speakerIsThirdParty,
      speakerIsDiamond,
      selectedContributorTypeCode,
      coupleFirstNames,
      coupleLastName,
      speakerAFirstName,
      speakerALastName,
      speakerBFirstName,
      speakerBLastName,
      speakerPhotoTemp,
      speakerAPhotoTemp,
      speakerBPhotoTemp,
      speakerPhotoPrev,
      speakerAPhotoPrev,
      speakerBPhotoPrev,
      introFiles,
      archivedIntroFiles,
      removedArchivedIntroFiles,
      introUploadedFilenames,
      speakerAudios,
      speakerAudioSearch,
      apiError,
    };

    history.push({
      pathname: `${baseDreamCastPath || "/dreamcast"}/media`,
      search: "?mode=selection&source=speakers",
      state: {
        preselectedMediaIds: speakerAudios.map((audio) => String(audio.id)),
        preselectedMediaAudios: speakerAudios,
        mediaSelectionReturnPath: location.pathname,
        mediaSelectionContributorName: currentSpeakerDisplayName || speakerName,
        speakerFormState,
      },
    });
  };

  const handleSpeakerAudioRemove = (assignmentId) => {
    setSpeakerAudios((current) =>
      current.filter((audio) => audio.assignmentId !== assignmentId),
    );
  };

  const handleSpeakerAudioEdit = (audioId) => {
    onEditAudio(audioId, {
      returnPath: location.pathname,
      speakerFormState: {
        editingSpeakerId,
        editingWasDraft,
        speakerName,
        speakerDetails,
        speakerPhoto,
        speakerAPhoto,
        speakerBPhoto,
        speakerImageType,
        speakerIsThirdParty,
        speakerIsDiamond,
        selectedContributorTypeCode,
        coupleFirstNames,
        coupleLastName,
        speakerAFirstName,
        speakerALastName,
        speakerBFirstName,
        speakerBLastName,
        speakerPhotoTemp,
        speakerAPhotoTemp,
        speakerBPhotoTemp,
        speakerPhotoPrev,
        speakerAPhotoPrev,
        speakerBPhotoPrev,
        introFiles,
        archivedIntroFiles,
        removedArchivedIntroFiles,
        introUploadedFilenames,
        speakerAudios,
        speakerAudioSearch,
        apiError,
      },
    });
  };

  const saveFromCreateForm = async (mode = "save") => {
    // Prevent save if any photo uploads are in progress
    if (
      speakerPhotoUploading ||
      speakerAPhotoUploading ||
      speakerBPhotoUploading
    ) {
      setApiError("Please wait for photo uploads to complete before saving.");
      return;
    }

    // Prevent save if any intro uploads are in progress
    if (introFilesUploading > 0) {
      setApiError("Please wait for intro uploads to complete before saving.");
      return;
    }

    const enteredName = speakerName.trim();
    const trimmedName = enteredName || "Untitled Speaker";
    const combinedSpeakerAName = joinNameParts(
      speakerAFirstName,
      speakerALastName,
    );
    const combinedSpeakerBName = joinNameParts(
      speakerBFirstName,
      speakerBLastName,
    );
    const combinedCoupleName = joinNameParts(coupleFirstNames, coupleLastName);
    const resolvedDisplayName =
      speakerImageType === "couple"
        ? enteredName || combinedCoupleName || trimmedName
        : combinedSpeakerAName || trimmedName;
    const speakerId = editingSpeakerId || `speaker-${Date.now()}`;
    const existingSpeaker = editingSpeakerId
      ? (speakers || []).find((speaker) =>
          speakerMatchesIdentifier(speaker, editingSpeakerId),
        )
      : null;
    const nextSpeaker = {
      id: speakerId,
      contributorId: existingSpeaker?.contributorId || null,
      photo: speakerPhoto || "",
      imageType: speakerImageType,
      speakerAPhoto:
        speakerImageType === "couple" ? speakerAPhoto || null : null,
      speakerBPhoto:
        speakerImageType === "couple" ? speakerBPhoto || null : null,
      name: resolvedDisplayName,
      contributorName:
        speakerImageType === "couple" ? coupleFirstNames : speakerAFirstName,
      lastName: speakerImageType === "couple" ? coupleLastName : speakerALastName,
      isThirdParty: speakerIsThirdParty,
      isDiamond: speakerIsDiamond,
      contributorTypeCode:
        selectedContributorTypeCode ||
        inferContributorTypeCode({
          imageType: speakerImageType,
          isDiamond: speakerIsDiamond,
          isThirdParty: speakerIsThirdParty,
        }),
      speakerAName: speakerImageType === "couple" ? combinedSpeakerAName : "",
      speakerBName: speakerImageType === "couple" ? combinedSpeakerBName : "",
      type: deriveTypeLabelFromTypeCode(selectedContributorTypeCode),
      language: existingSpeaker?.language || "english",
      isDraft: mode === "draft",
      details: speakerDetails,
      // Intros live in the user's Bunny folder — no URL tracking in the DB.
      intros: [],
      archivedIntros: [],
      audios: speakerAudios,
    };

    setSavingState(mode);
    try {
      // For edits use the existing ID; for creates use the ID returned by the API.
      let resolvedSpeakerId = existingSpeaker?.contributorId
        ? parseInt(existingSpeaker.contributorId, 10) || null
        : parseInt(existingSpeaker?.id, 10) || null;

      if (editingSpeakerId) {
        if (!resolvedSpeakerId) {
          throw new Error(
            "Unable to resolve speaker id for update. Please reopen this speaker and try again.",
          );
        }
        const apiPayload = mapSpeakerToAPI(nextSpeaker, {
          includeContributorId: true,
        });
        await updateDreamcastSpeaker(resolvedSpeakerId, apiPayload);
      } else {
        const apiPayload = mapSpeakerToAPI(nextSpeaker, {
          includeContributorId: false,
        });
        const createResponse = await createDreamcastSpeaker(apiPayload);
        resolvedSpeakerId = createResponse?.data?.contributorId || null;
      }

      // Queue file operations to happen in background (optimistic UI)
      let hasQueuedFileOps = false;
      if (resolvedSpeakerId) {
        const ops = {};
        if (introUploadedFilenames.length > 0) {
          ops.intros = {
            speakerId: resolvedSpeakerId,
            filenames: introUploadedFilenames,
          };
        }
        if (removedArchivedIntroFiles.length > 0) {
          ops.deletedIntros = {
            speakerId: resolvedSpeakerId,
            introUrls: removedArchivedIntroFiles
              .map((file) => file?.previewUrl)
              .filter(Boolean),
          };
        }
        if (speakerPhotoTemp || speakerAPhotoTemp || speakerBPhotoTemp) {
          const photosToRename = [];
          if (speakerPhotoTemp) {
            photosToRename.push({
              tempFilename: speakerPhotoTemp,
              photoType: "",
              oldUrl: speakerPhotoPrev || undefined,
            });
          }
          if (speakerAPhotoTemp && speakerImageType === "couple") {
            photosToRename.push({
              tempFilename: speakerAPhotoTemp,
              photoType: "_a",
              oldUrl: speakerAPhotoPrev || undefined,
            });
          }
          if (speakerBPhotoTemp && speakerImageType === "couple") {
            photosToRename.push({
              tempFilename: speakerBPhotoTemp,
              photoType: "_b",
              oldUrl: speakerBPhotoPrev || undefined,
            });
          }
          if (photosToRename.length > 0) {
            ops.photos = {
              speakerId: resolvedSpeakerId,
              photos: photosToRename,
            };
          }
        }
        if (Object.keys(ops).length > 0) {
          hasQueuedFileOps = true;
          setPendingFileOps(ops);
        }
      }

      const resolvedContributorId = String(resolvedSpeakerId);
      const optimisticSpeaker = {
        ...nextSpeaker,
        id: resolvedContributorId,
        contributorId: resolvedContributorId,
        rowId: existingSpeaker?.rowId || `${resolvedContributorId}::0`,
      };
      setSpeakers((current) =>
        existingSpeaker
          ? current.map((speaker) =>
              speakerMatchesIdentifier(speaker, editingSpeakerId)
                ? optimisticSpeaker
                : speaker,
            )
          : [...current, optimisticSpeaker],
      );

      // Optimistic UI: close form immediately and trigger a refresh
      setIsCreateView(false);
      resetCreateForm();
      setApiError("");

      if (!hasQueuedFileOps) {
        loadSpeakers().catch((err) => {
          console.error("[SpeakerList] Failed to refresh speakers:", err);
        });
      }
    } catch (error) {
      setApiError(error.message || "Failed to save speaker. Please try again.");
    } finally {
      setSavingState(null);
    }
  };

  const handleCancelCreate = async () => {
    // Delete temp photos if user cancels
    const tempPhotos = [];
    if (speakerPhotoTemp) tempPhotos.push(speakerPhotoTemp);
    if (speakerAPhotoTemp) tempPhotos.push(speakerAPhotoTemp);
    if (speakerBPhotoTemp) tempPhotos.push(speakerBPhotoTemp);

    if (tempPhotos.length > 0) {
      try {
        await deleteSpeakerTempPhotos(tempPhotos);
      } catch (error) {
        console.warn("Failed to delete temp photos:", error);
      }
    }

    // Delete uploaded intro files if user cancels
    if (introUploadedFilenames.length > 0) {
      try {
        await deleteSpeakerTempIntros(introUploadedFilenames);
      } catch (error) {
        console.warn("Failed to delete uploaded intro files:", error);
      }
    }

    setIsCreateView(false);
    resetCreateForm();
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirmation(true);
  };

  const handleConfirmDelete = async () => {
    if (!editingSpeakerId) {
      console.warn("[SpeakerList] No editingSpeakerId");
      return;
    }

    setIsDeletingState(true);

    try {
      // Get the existing speaker to extract the correct ID (same as PUT operation)
      const existingSpeaker = (speakers || []).find((speaker) =>
        speakerMatchesIdentifier(speaker, editingSpeakerId),
      );

      // Use the same ID resolution as saveFromCreateForm
      const apiSpeakerId = existingSpeaker?.contributorId
        ? parseInt(existingSpeaker.contributorId, 10) || null
        : parseInt(existingSpeaker?.id, 10) || null;

      if (!apiSpeakerId) {
        throw new Error("Unable to determine speaker ID for deletion");
      }

      // Call the API to delete the speaker
      await deleteDreamcastSpeaker(apiSpeakerId);

      // Close modal and return to list
      setShowDeleteConfirmation(false);
      setIsCreateView(false);
      resetCreateForm();
      setApiError("");
      setNewlySavedSpeakerId(null);

      // Refresh the speaker list from API (this sets isLoadingList automatically)
      // Loader will display while fetching and disappear when complete
      await loadSpeakers();
    } catch (error) {
      console.error("[SpeakerList] Failed to delete speaker:", error);
      setApiError(
        `Failed to delete speaker: ${error.message || "Unknown error"}`,
      );
      setIsLoadingList(false);
    } finally {
      setIsDeletingState(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirmation(false);
  };

  const handleSpeakerSelectToggle = (speakerId) => {
    setSelectedSpeakerIds((current) =>
      current.includes(speakerId)
        ? current.filter((id) => id !== speakerId)
        : [...current, speakerId],
    );
  };

  const handleSpeakerEdit = async (speakerId) => {
    setLoadingSpeakerId(speakerId);
    try {
      const selectedSpeaker = (speakers || []).find((speaker) =>
        speakerMatchesIdentifier(speaker, speakerId),
      );
      const apiSpeakerId =
        selectedSpeaker?.contributorId || selectedSpeaker?.id || speakerId;
      const response = await fetchDreamcastSpeakerById(apiSpeakerId);

      // Extract speaker data from API response
      // New API format: { success: true, data: {...speaker data...} }
      const apiSpeaker = response?.data || response;

      if (apiSpeaker && (apiSpeaker.contributorId || apiSpeaker.id)) {
        const fullSpeaker = mapSpeakerFromAPI(apiSpeaker);
        if (fullSpeaker) {
          await openEditView(fullSpeaker);
          onEditSpeaker(fullSpeaker);
        } else {
          throw new Error("Failed to map speaker data from API");
        }
      } else {
        throw new Error("Invalid speaker data received from API");
      }
    } catch (error) {
      const fallback = (speakers || []).find(
        (s) => (s.rowId || s.id) === String(speakerId),
      );

      if (fallback) {
        await openEditView(fallback);
        onEditSpeaker(fallback);
      } else {
        setApiError("Failed to load speaker details. Please try again.");
      }
    } finally {
      setLoadingSpeakerId(null);
    }
  };

  useEffect(() => {
    if (!isSelectionMode) {
      return;
    }

    if (!preselectedSpeakerIds.length) {
      setSelectedSpeakerIds([]);
      setInitialSelectedRowIds([]);
      return;
    }

    const preselectedIdSet = new Set(preselectedSpeakerIds.map(String));
    const matchedRowIds = (speakers || [])
      .filter((speaker) =>
        preselectedIdSet.has(String(speaker.contributorId || speaker.id)),
      )
      .map((speaker) => speaker.rowId || speaker.id);

    setSelectedSpeakerIds(matchedRowIds);
    setInitialSelectedRowIds(matchedRowIds);
  }, [isSelectionMode, preselectedSpeakerIds, speakers]);

  useEffect(() => {
    if (!location.state?.fromMediaSelection) {
      return;
    }

    const speakerFormState =
      location.state?.speakerFormState &&
      typeof location.state.speakerFormState === "object"
        ? location.state.speakerFormState
        : null;

    if (speakerFormState) {
      setEditingSpeakerId(speakerFormState.editingSpeakerId ?? null);
      setEditingWasDraft(Boolean(speakerFormState.editingWasDraft));
      setSpeakerName(speakerFormState.speakerName || "");
      setSpeakerDetails({
        ...DEFAULT_SPEAKER_DETAILS,
        ...(speakerFormState.speakerDetails || {}),
      });

      setSpeakerPhoto(speakerFormState.speakerPhoto || null);
      setSpeakerAPhoto(speakerFormState.speakerAPhoto || null);
      setSpeakerBPhoto(speakerFormState.speakerBPhoto || null);
      setSpeakerImageType(speakerFormState.speakerImageType || "individual");
      setSpeakerIsThirdParty(Boolean(speakerFormState.speakerIsThirdParty));
      setSpeakerIsDiamond(Boolean(speakerFormState.speakerIsDiamond));
      setSelectedContributorTypeCode(
        speakerFormState.selectedContributorTypeCode || "",
      );

      setCoupleFirstNames(speakerFormState.coupleFirstNames || "");
      setCoupleLastName(speakerFormState.coupleLastName || "");
      setSpeakerAFirstName(speakerFormState.speakerAFirstName || "");
      setSpeakerALastName(speakerFormState.speakerALastName || "");
      setSpeakerBFirstName(speakerFormState.speakerBFirstName || "");
      setSpeakerBLastName(speakerFormState.speakerBLastName || "");

      setSpeakerPhotoTemp(speakerFormState.speakerPhotoTemp || null);
      setSpeakerAPhotoTemp(speakerFormState.speakerAPhotoTemp || null);
      setSpeakerBPhotoTemp(speakerFormState.speakerBPhotoTemp || null);
      setSpeakerPhotoPrev(speakerFormState.speakerPhotoPrev || null);
      setSpeakerAPhotoPrev(speakerFormState.speakerAPhotoPrev || null);
      setSpeakerBPhotoPrev(speakerFormState.speakerBPhotoPrev || null);

      const nextIntroFiles = Array.isArray(speakerFormState.introFiles)
        ? speakerFormState.introFiles
        : [];
      const nextArchivedIntroFiles = Array.isArray(
        speakerFormState.archivedIntroFiles,
      )
        ? speakerFormState.archivedIntroFiles
        : [];

      setIntroFiles(nextIntroFiles);
      setArchivedIntroFiles(nextArchivedIntroFiles);
      introFilesRef.current = nextIntroFiles;
      archivedIntroFilesRef.current = nextArchivedIntroFiles;

      setRemovedArchivedIntroFiles(
        Array.isArray(speakerFormState.removedArchivedIntroFiles)
          ? speakerFormState.removedArchivedIntroFiles
          : [],
      );
      setIntroUploadedFilenames(
        Array.isArray(speakerFormState.introUploadedFilenames)
          ? speakerFormState.introUploadedFilenames
          : [],
      );

      setSpeakerAudios(
        ensureSpeakerAudioAssignmentIds(
          Array.isArray(speakerFormState.speakerAudios)
            ? speakerFormState.speakerAudios
            : [],
        ),
      );
      setApiError(speakerFormState.apiError || "");
    }

    if (Array.isArray(location.state.selectedMediaAudios)) {
      setSpeakerAudios(
        ensureSpeakerAudioAssignmentIds(location.state.selectedMediaAudios),
      );
    }

    // Show the complete assigned-audio list after returning from selection.
    setSpeakerAudioSearch("");

    setIsCreateView(true);
    history.replace({ ...location, state: undefined });
  }, [history, location]);

  // Process pending file operations in the background (optimistic UI)
  useEffect(() => {
    if (!pendingFileOps) {
      return;
    }

    const processPendingOps = async () => {
      try {
        const promises = [];

        if (pendingFileOps.intros) {
          promises.push(
            moveSpeakerTempIntros(
              pendingFileOps.intros.speakerId,
              pendingFileOps.intros.filenames,
            ),
          );
        }

        if (pendingFileOps.photos) {
          promises.push(
            renameSpeakerPhotos(
              pendingFileOps.photos.speakerId,
              pendingFileOps.photos.photos,
            ),
          );
        }

        if (
          pendingFileOps.deletedIntros &&
          Array.isArray(pendingFileOps.deletedIntros.introUrls) &&
          pendingFileOps.deletedIntros.introUrls.length > 0
        ) {
          promises.push(
            deleteDreamcastSpeakerIntros(
              pendingFileOps.deletedIntros.speakerId,
              pendingFileOps.deletedIntros.introUrls,
            ),
          );
        }

        await Promise.all(promises);
      } catch (error) {
        console.error("[SpeakerList] Background file operation failed:", error);
        setBackgroundNotification({
          type: "error",
          message: `File processing error: ${error.message || "Unknown error"}`,
        });
      } finally {
        try {
          await loadSpeakers({ showLoader: false, preloadImages: true });
        } catch (error) {
          console.error("[SpeakerList] Failed to refresh speakers:", error);
        }
        setPendingFileOps(null);
        // Auto-dismiss notification after 5 seconds
        setTimeout(() => setBackgroundNotification(null), 5000);
      }
    };

    processPendingOps();
  }, [loadSpeakers, pendingFileOps]);

  // Clear edit attempt tracking when file operations complete
  useEffect(() => {
    if (!pendingFileOps && editAttemptedDuringSpeakerIds.size > 0) {
      // File operations completed - auto-open edit view for speakers that were attempted
      const speakerId = Array.from(editAttemptedDuringSpeakerIds)[0];
      const speaker = speakers.find((s) => String(s.id) === speakerId);

      if (speaker) {
        openEditView(speaker);
      }

      setEditAttemptedDuringSpeakerIds(new Set());
    }
  }, [pendingFileOps, editAttemptedDuringSpeakerIds, speakers]);

  const returnToSelectionTarget = (ids) => {
    const baseDreamCastPath = path.replace(/\/speaker-list$/, "");
    const selectedLookup = new Set(ids);
    const selectedSpeakers = (speakers || []).filter((speaker) =>
      selectedLookup.has(speaker.rowId || speaker.id),
    );
    const mediaFormState = location.state?.mediaFormState;

    const fallbackPathBySource = {
      audios: `${baseDreamCastPath || "/dreamcast"}/audios`,
      "digital-delivery-add": `${
        baseDreamCastPath || "/dreamcast"
      }/digital-delivery/add`,
      "digital-delivery-edit": `${
        baseDreamCastPath || "/dreamcast"
      }/digital-delivery`,
    };

    const nextPath =
      speakerSelectionReturnPath ||
      fallbackPathBySource[selectionSource] ||
      `${baseDreamCastPath || "/dreamcast"}/audios`;

    history.push({
      pathname: nextPath,
      state: {
        fromSpeakerSelection: true,
        selectedSpeakers,
        ...(mediaFormState && typeof mediaFormState === "object"
          ? { mediaFormState }
          : {}),
      },
    });
  };

  const handleSelectAllDisplayed = () => {
    setSelectedSpeakerIds((current) => {
      const next = new Set(current);
      visibleSpeakers.forEach((speaker) =>
        next.add(speaker.rowId || speaker.id),
      );
      return Array.from(next);
    });
  };

  const handleUnselectAll = () => {
    setSelectedSpeakerIds([]);
  };

  return (
    <div ref={pageRef} className={styles.page}>
      <ControlsHeader title={activeHeaderTitle} />
      {apiError && <p className={styles.apiError}>{apiError}</p>}

      {backgroundNotification && (
        <div
          className={
            backgroundNotification.type === "error"
              ? styles.notificationError
              : styles.notificationSuccess
          }
        >
          {backgroundNotification.message}
        </div>
      )}

      {!isCreateView ? (
        isLoadingList && speakers.length === 0 ? (
          <div className={styles.initialLoaderOverlay}>
            <Loader color="#415364" size={85} />
          </div>
        ) : (
          <>
            {isLoadingList && (
              <div className={styles.initialLoaderOverlay}>
                <Loader color="#415364" size={85} />
              </div>
            )}
            <div className={styles.listLayout}>
              <div
                className={
                  isSelectionMode ? styles.selectionToolbar : styles.toolbar
                }
              >
                <SearchBar
                  placeholderText="Search Speakers"
                  value={searchQuery}
                  onInput={setSearchQuery}
                  noResults={noResults}
                  minChar={0}
                  searchDelay={0}
                />

                {!isSelectionMode && (
                  <Button className={styles.addButton} onClick={openCreateView}>
                    Add New Speaker
                  </Button>
                )}

                <SortModal
                  options={SORT_OPTIONS}
                  selected={sortBy}
                  onChange={setSortBy}
                  container={pageRef}
                />

                {isSelectionMode && (
                  <span className={styles.selectedCount}>
                    Selected: {selectedSpeakerIds.length}
                  </span>
                )}

                {isSelectionMode && (
                  <div className={styles.selectionActions}>
                    <Button
                      className={styles.addSelectedButton}
                      onClick={() =>
                        returnToSelectionTarget(selectedSpeakerIds)
                      }
                      disabled={selectedSpeakerIds.length === 0}
                      preset={ButtonPreset.addWithIcon}
                      icon={<img src={PlusSquareIcon} alt="" />}
                    >
                      Add
                    </Button>
                    <Button
                      className={styles.cancelSelectButton}
                      onClick={() =>
                        selectionSource === "audios" ||
                        selectionSource === "digital-delivery-add" ||
                        selectionSource === "digital-delivery-edit"
                          ? returnToSelectionTarget(initialSelectedRowIds)
                          : setSelectedSpeakerIds(initialSelectedRowIds)
                      }
                    >
                      Cancel
                    </Button>
                    <Button
                      className={styles.selectAllButton}
                      onClick={handleSelectAllDisplayed}
                    >
                      Select All
                    </Button>
                    <Button
                      className={styles.unselectAllButton}
                      onClick={handleUnselectAll}
                    >
                      Unselect All
                    </Button>
                  </div>
                )}
              </div>

              <div
                className={
                  isSelectionMode ? styles.selectionBody : styles.listBody
                }
              >
                <FilterColumn
                  sections={filterSections}
                  selected={filters}
                  onChange={setFilters}
                  className={styles.filterColumn}
                />

                <section
                  className={
                    isSelectionMode
                      ? styles.selectionListContent
                      : styles.listContent
                  }
                  aria-label="Speaker list results"
                >
                  <div
                    className={
                      isSelectionMode ? styles.selectionRows : styles.rows
                    }
                  >
                    {visibleSpeakers.map((speaker) => (
                      <div
                        key={speaker.rowId || speaker.id}
                        className={
                          isSelectionMode
                            ? styles.selectionRowWrap
                            : styles.rowWrap
                        }
                      >
                        <SpeakerListItem
                          id={speaker.rowId || speaker.id}
                          photo={speaker.photo}
                          name={speaker.name}
                          isSelectionMode={isSelectionMode}
                          isSelected={selectedSpeakerIds.includes(
                            speaker.rowId || speaker.id,
                          )}
                          isLoading={
                            loadingSpeakerId ===
                              (speaker.rowId || speaker.id) ||
                            editAttemptedDuringSpeakerIds.has(
                              String(speaker.rowId || speaker.id),
                            )
                          }
                          disabled={
                            loadingSpeakerId ===
                              (speaker.rowId || speaker.id) ||
                            editAttemptedDuringSpeakerIds.size > 0
                          }
                          isNewlySaved={
                            newlySavedSpeakerId ===
                            (speaker.rowId || speaker.id)
                          }
                          onEdit={handleSpeakerEdit}
                          onSelect={handleSpeakerSelectToggle}
                        />
                      </div>
                    ))}
                  </div>


                  {filtersNoResults ? (
                    <p className={styles.emptyState}>No results found.</p>
                  ) : !visibleSpeakers.length ? (
                    <p className={styles.emptyState}>
                      No matching speakers found.
                    </p>
                  ) : null}
                </section>
              </div>
            </div>
          </>
        )
      ) : (
        <section className={styles.createLayout}>
          {isLoadingList && (
            <div className={styles.initialLoaderOverlay}>
              <Loader color="#415364" size={85} />
            </div>
          )}
          <div className={styles.actionBarRow}>
            <FormActionBar
              onSave={() => saveFromCreateForm("save")}
              onSaveDraft={() => saveFromCreateForm("draft")}
              onCancel={handleCancelCreate}
              onDelete={editingSpeakerId ? handleDeleteClick : null}
              isDraft={editingWasDraft}
              isSaving={savingState}
              disabled={
                speakerPhotoUploading ||
                speakerAPhotoUploading ||
                speakerBPhotoUploading ||
                introFilesUploading > 0 ||
                isLoadingList
              }
              embedded
            />
          </div>

          <div className={styles.formColumns}>
            <div className={styles.leftColumn}>
              <SpeakerTypeForm
                contributorTypes={contributorTypes}
                contributorTypeCode={selectedContributorTypeCode}
                onContributorTypeCodeChange={setSelectedContributorTypeCode}
              />

              {!isChooseSelected && (
                <>
                  <SpeakerNameForm
                    name={speakerName}
                    onChange={setSpeakerName}
                    imageType={speakerImageType}
                    individualFirstName={speakerAFirstName}
                    individualLastName={speakerALastName}
                    onIndividualFirstNameChange={
                      handleIndividualFirstNameChange
                    }
                    onIndividualLastNameChange={handleIndividualLastNameChange}
                    coupleFirstNames={coupleFirstNames}
                    coupleLastName={coupleLastName}
                    onCoupleFirstNamesChange={setCoupleFirstNames}
                    onCoupleLastNameChange={setCoupleLastName}
                    speakerAFirstName={speakerAFirstName}
                    speakerALastName={speakerALastName}
                    speakerBFirstName={speakerBFirstName}
                    speakerBLastName={speakerBLastName}
                    onSpeakerAFirstNameChange={setSpeakerAFirstName}
                    onSpeakerALastNameChange={setSpeakerALastName}
                    onSpeakerBFirstNameChange={setSpeakerBFirstName}
                    onSpeakerBLastNameChange={setSpeakerBLastName}
                  />

                  <SpeakerImageUpload
                    photo={speakerPhoto}
                    imageType={speakerImageType}
                    speakerAPhoto={speakerAPhoto}
                    speakerBPhoto={speakerBPhoto}
                    onUpload={async (file) => {
                      const localUrl = URL.createObjectURL(file);
                      setSpeakerPhoto(localUrl);
                      setSpeakerPhotoUploading(true);
                      try {
                        const response = await uploadDreamcastSpeakerPhotoFile(
                          file,
                          "",
                        );
                        if (response?.data?.thumbnailUrl) {
                          setSpeakerPhoto(response.data.thumbnailUrl);
                          setSpeakerPhotoTemp(response.tempFilename);
                        }
                      } catch {
                        // keep local blob URL if CDN upload fails
                      } finally {
                        setSpeakerPhotoUploading(false);
                      }
                    }}
                    onUploadSpeakerA={async (file) => {
                      const localUrl = URL.createObjectURL(file);
                      setSpeakerAPhoto(localUrl);
                      setSpeakerAPhotoUploading(true);
                      try {
                        const response = await uploadDreamcastSpeakerPhotoFile(
                          file,
                          "_a",
                        );
                        if (response?.data?.thumbnailUrl) {
                          setSpeakerAPhoto(response.data.thumbnailUrl);
                          setSpeakerAPhotoTemp(response.tempFilename);
                        }
                      } catch {
                        // keep local blob URL if CDN upload fails
                      } finally {
                        setSpeakerAPhotoUploading(false);
                      }
                    }}
                    onUploadSpeakerB={async (file) => {
                      const localUrl = URL.createObjectURL(file);
                      setSpeakerBPhoto(localUrl);
                      setSpeakerBPhotoUploading(true);
                      try {
                        const response = await uploadDreamcastSpeakerPhotoFile(
                          file,
                          "_b",
                        );
                        if (response?.data?.thumbnailUrl) {
                          setSpeakerBPhoto(response.data.thumbnailUrl);
                          setSpeakerBPhotoTemp(response.tempFilename);
                        }
                      } catch {
                        // keep local blob URL if CDN upload fails
                      } finally {
                        setSpeakerBPhotoUploading(false);
                      }
                    }}
                    onRemove={() => {
                      setSpeakerPhoto(null);
                      setSpeakerPhotoTemp(null);
                    }}
                    onRemoveSpeakerA={() => {
                      setSpeakerAPhoto(null);
                      setSpeakerAPhotoTemp(null);
                    }}
                    onRemoveSpeakerB={() => {
                      setSpeakerBPhoto(null);
                      setSpeakerBPhotoTemp(null);
                    }}
                  />

                  <SpeakerDetailsForm
                    {...speakerDetails}
                    imageType={speakerImageType}
                    contributorType={selectedContributorType}
                    onChange={(field, value) =>
                      setSpeakerDetails((current) => ({
                        ...current,
                        [field]: value,
                      }))
                    }
                  />
                </>
              )}
            </div>

            <div className={styles.rightColumn}>
              {ENABLE_SPEAKER_INTRO_UPLOAD && !isChooseSelected && (
                <FileUploadSection
                  label="Upload Intro"
                  onSave={handleIntroSave}
                  showManagedFiles
                  initialFiles={introFiles}
                  initialArchivedFiles={archivedIntroFiles}
                  onArchivedFileRemove={(file) => {
                    if (!file?.id) {
                      return;
                    }

                    setRemovedArchivedIntroFiles((existing) => {
                      const alreadyTracked = existing.some(
                        (item) => item?.id === file.id,
                      );
                      return alreadyTracked ? existing : [...existing, file];
                    });
                  }}
                  onFilesChange={({ files, archivedFiles }) => {
                    introFilesRef.current = files;
                    archivedIntroFilesRef.current = archivedFiles;
                    setIntroFiles(files);
                    setArchivedIntroFiles(archivedFiles);
                  }}
                  uploadFile={(file, signal) =>
                    uploadDreamcastSpeakerIntroFile(file, signal)
                  }
                  onUploadStart={() => setIntroFilesUploading((c) => c + 1)}
                  onUploadEnd={() =>
                    setIntroFilesUploading((c) => Math.max(0, c - 1))
                  }
                />
              )}

              <SpeakerAudiosSection
                audios={speakerAudios}
                searchValue={speakerAudioSearch}
                onSearch={setSpeakerAudioSearch}
                onAdd={handleSpeakerAudioAdd}
                onRemove={handleSpeakerAudioRemove}
                onEdit={handleSpeakerAudioEdit}
              />
            </div>
          </div>
        </section>
      )}

      <Modal
        title="Delete Speaker"
        isOpen={showDeleteConfirmation}
        onClose={handleCancelDelete}
        closeBtn="none"
        className={styles.deleteConfirmModal}
      >
        <div className={styles.deleteModalBody}>
          <p>
            Are you sure you want to delete this speaker? This action cannot be
            reversed.
          </p>
          <div className={styles.deleteModalActions}>
            <Button onClick={handleCancelDelete} disabled={isDeletingState}>
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDelete}
              disabled={isDeletingState}
              style={{
                backgroundColor: "var(--CMS-Red, #D4342A)",
                color: "#fff",
              }}
            >
              Delete
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

SpeakerList.propTypes = {
  headerTitle: PropTypes.string,
  initialSpeakers: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      photo: PropTypes.string,
      imageType: PropTypes.oneOf(["individual", "couple"]),
      speakerAPhoto: PropTypes.string,
      speakerBPhoto: PropTypes.string,
      contributorTypeCode: PropTypes.string,
      contributorType: PropTypes.string,
      name: PropTypes.string.isRequired,
      type: PropTypes.oneOf(["diamond", "3rd"]),
      language: PropTypes.oneOf(["english", "spanish", "korean"]),
    }),
  ),
  disableInitialFetch: PropTypes.bool,
  initialEditingSpeakerId: PropTypes.string,
  onAddNewSpeaker: PropTypes.func,
  onEditSpeaker: PropTypes.func,
  onEditAudio: PropTypes.func,
};

SpeakerList.defaultProps = {
  headerTitle: "DreamCast - Speaker List",
  initialSpeakers: null,
  disableInitialFetch: false,
  initialEditingSpeakerId: null,
  onAddNewSpeaker: () => {},
  onEditSpeaker: () => {},
  onEditAudio: () => {},
};

export default SpeakerList;
